import { useCallback, useEffect, useRef, useState } from 'react';

import { Icon, type IconName } from '@/components/ui/icon';
import { Popover } from '@/components/ui/popover';
import { useClickOutside, useEscapeKey } from '@/hooks';
import { cn } from '@/lib/cn';

export interface ActionMenuItem {
  label: string;
  icon?: IconName;
  onSelect: () => void;
  /** Renders in the danger colour and sits below a divider. */
  destructive?: boolean;
  disabled?: boolean;
  /** Explains why the item is disabled. */
  title?: string;
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  label?: string;
  align?: 'start' | 'end';
  /**
   * Padding for the trigger, where the default does not suit.
   *
   * The default is sized for a dense table row. In the topbar it sits beside
   * other chrome buttons and has to match them instead.
   */
  triggerClassName?: string;
}

/**
 * The row-level "..." menu.
 *
 * Menu semantics are provided in full - roving focus with arrow keys, Home/End,
 * Escape to close and return focus to the trigger - because a div-based menu
 * without them is unusable by keyboard.
 */
export function ActionMenu({
  items,
  label = 'Open actions',
  align = 'end',
  triggerClassName,
}: ActionMenuProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  useClickOutside([triggerRef, menuRef], close, open);
  useEscapeKey(() => {
    close();
    triggerRef.current?.focus();
  }, open);

  const enabledIndexes = items
    .map((item, index) => (item.disabled ? -1 : index))
    .filter((index) => index >= 0);

  /* Opening seeds the active item here rather than in an effect: doing it in
     the handler avoids a second render pass on every open. */
  function openMenu(): void {
    setActiveIndex(enabledIndexes[0] ?? -1);
    setOpen(true);
  }

  useEffect(() => {
    if (!open || activeIndex < 0) {
      return;
    }

    function focusItem(): void {
      menuRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)?.focus();
    }

    focusItem();

    /* Then again on the next turn of the loop. Popover leaves its panel
       `visibility: hidden` until it has measured it, and a hidden element
       refuses focus - so the first attempt lands nowhere whenever measuring
       has not finished, and the menu would open unusable by keyboard. */
    const retry = window.setTimeout(focusItem, 0);

    return () => {
      window.clearTimeout(retry);
    };
  }, [open, activeIndex]);

  function move(delta: number): void {
    if (enabledIndexes.length === 0) {
      return;
    }

    const position = enabledIndexes.indexOf(activeIndex);
    const next =
      position === -1 ? 0 : (position + delta + enabledIndexes.length) % enabledIndexes.length;

    setActiveIndex(enabledIndexes[next] ?? -1);
  }

  function select(item: ActionMenuItem): void {
    if (item.disabled) {
      return;
    }

    close();
    triggerRef.current?.focus();
    item.onSelect();
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={(event) => {
          event.stopPropagation();
          if (open) {
            close();
          } else {
            openMenu();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openMenu();
          }
        }}
        className={cn(
          'rounded-sm text-content-muted transition-colors hover:bg-surface-muted hover:text-content',
          /* Dense on a desktop table row, a proper target under a thumb. */
          'p-2 sm:p-1',
          open && 'bg-surface-muted text-content',
          triggerClassName,
        )}
      >
        <Icon name="more-horizontal" size={15} />
      </button>

      <Popover anchorRef={triggerRef} open={open} align={align} className="min-w-44 py-1">
        <div
          ref={menuRef}
          role="menu"
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              move(1);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              move(-1);
            } else if (event.key === 'Home') {
              event.preventDefault();
              setActiveIndex(enabledIndexes[0] ?? -1);
            } else if (event.key === 'End') {
              event.preventDefault();
              setActiveIndex(enabledIndexes[enabledIndexes.length - 1] ?? -1);
            }
          }}
        >
          {items.map((item, index) => (
            <div key={item.label}>
              {item.destructive && index > 0 && !items[index - 1]?.destructive && (
                <div className="my-1 h-px bg-border" role="separator" />
              )}

              <button
                type="button"
                role="menuitem"
                data-index={index}
                tabIndex={-1}
                disabled={item.disabled}
                title={item.title}
                onClick={() => {
                  select(item);
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm outline-none',
                  'disabled:cursor-not-allowed disabled:opacity-45',
                  item.destructive
                    ? 'text-danger hover:bg-danger-soft focus:bg-danger-soft'
                    : 'text-content hover:bg-surface-muted focus:bg-surface-muted',
                )}
              >
                {item.icon && <Icon name={item.icon} size={14} className="shrink-0" />}
                {item.label}
              </button>
            </div>
          ))}

          {items.length === 0 && (
            <p className="px-2.5 py-1.5 text-xs text-content-subtle">No actions available</p>
          )}
        </div>
      </Popover>
    </>
  );
}
